import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import PersonIcon from '@mui/icons-material/Person';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  formatPodcastTime,
  getPodcastAuthorName,
  getPodcastLikesCount,
  resolvePodcastAuthorPhoto,
  resolvePodcastThumbnail,
  resolvePodcastVideo,
} from '../utils/podcastUtils';

const VideoPodcastCard = ({
  podcast,
  to,
  showOwnerActions = false,
  onEdit,
  onDelete,
  className = '',
}) => {
  const videoRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [previewActive, setPreviewActive] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);

  const detailPath = to || `/video-podcasts/${podcast._id}`;
  const thumbnailUrl = useMemo(() => resolvePodcastThumbnail(podcast), [podcast]);
  const previewUrl = useMemo(() => resolvePodcastVideo(podcast), [podcast]);
  const authorName = getPodcastAuthorName(podcast);
  const authorPhoto = resolvePodcastAuthorPhoto(podcast);
  const likesCount = getPodcastLikesCount(podcast);
  const viewsCount = podcast?.views || 0;
  const uploadTime = formatPodcastTime(podcast?.publishedAt || podcast?.createdAt);
  const isExternalVideo = Boolean(
    podcast?.video && (podcast.video.startsWith('http://') || podcast.video.startsWith('https://'))
  );
  const canPreview = Boolean(previewUrl && !isExternalVideo);

  const startPreview = () => {
    if (!canPreview) return;
    setPreviewActive(true);
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }
  };

  const stopPreview = () => {
    setPreviewActive(false);
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };

  return (
    <article
      className={`group relative overflow-hidden rounded-2xl border border-app-border bg-app-card shadow-sm transition hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-lg ${className}`}
      onMouseEnter={startPreview}
      onMouseLeave={stopPreview}
    >
      <Link to={detailPath} className="block">
        <div className="relative aspect-video overflow-hidden bg-slate-900">
          {previewActive && canPreview ? (
            <video
              ref={videoRef}
              src={previewUrl}
              muted
              playsInline
              loop
              preload="metadata"
              poster={thumbnailUrl || undefined}
              className="h-full w-full object-cover"
            />
          ) : thumbnailUrl && !thumbFailed ? (
            <img
              src={thumbnailUrl}
              alt={podcast.title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              onError={() => setThumbFailed(true)}
            />
          ) : canPreview ? (
            <video
              ref={videoRef}
              src={previewUrl}
              muted
              playsInline
              preload="metadata"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-800 to-slate-950">
              <VideoLibraryIcon className="h-16 w-16 text-slate-500" />
            </div>
          )}

          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 transition group-hover:bg-black/30">
            <div className="flex h-14 w-14 scale-90 items-center justify-center rounded-full bg-white/95 opacity-0 shadow-lg transition group-hover:scale-100 group-hover:opacity-100">
              <PlayArrowIcon className="ml-0.5 text-slate-900" sx={{ fontSize: 36 }} />
            </div>
          </div>

          {podcast?.category && (
            <span className="absolute left-3 top-3 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
              {podcast.category}
            </span>
          )}

          {!podcast?.isPublished && (
            <span className="absolute right-3 top-3 rounded-full bg-amber-500 px-2.5 py-1 text-[11px] font-semibold text-white">
              Draft
            </span>
          )}
        </div>

        <div className="space-y-3 p-4">
          <h3 className="line-clamp-2 text-base font-semibold text-app-text transition group-hover:text-sky-600 dark:group-hover:text-sky-400">
            {podcast.title}
          </h3>

          <div className="flex items-center gap-2">
            {authorPhoto ? (
              <img
                src={authorPhoto}
                alt={authorName}
                className="h-8 w-8 rounded-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700">
                <PersonIcon className="h-4 w-4 text-slate-500" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-app-text">{authorName}</p>
              <p className="text-xs text-app-muted">{uploadTime}</p>
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs text-app-muted">
            <span className="inline-flex items-center gap-1">
              <VisibilityIcon sx={{ fontSize: 14 }} />
              {viewsCount}
            </span>
            <span className="inline-flex items-center gap-1">
              <ThumbUpIcon sx={{ fontSize: 14 }} />
              {likesCount}
            </span>
          </div>
        </div>
      </Link>

      {showOwnerActions && (
        <div className="absolute right-2 top-2 z-20">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuOpen((prev) => !prev);
            }}
            className="rounded-full bg-black/60 p-1.5 text-white transition hover:bg-black/80"
            aria-label="Podcast options"
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-10 z-30 w-36 overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-xl">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenuOpen(false);
                  onEdit?.(podcast);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-app-text hover:bg-app-hover"
              >
                <EditIcon sx={{ fontSize: 16 }} />
                Edit
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenuOpen(false);
                  onDelete?.(podcast);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
                Delete
              </button>
            </div>
          )}
        </div>
      )}
    </article>
  );
};

export default VideoPodcastCard;
